import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
  TagIcon,
  CalendarIcon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  BeakerIcon,
  PhotoIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  ScissorsIcon,
  UserGroupIcon,
  PhoneIcon,
  CheckCircleIcon,
  EyeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../config/api';
import { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

// Types
interface DocumentCategory {
  name: string;
  description: string;
  icon: string;
  subcategories: string[];
}

interface HealthDocument {
  id: string;
  filename: string;
  category: string;
  provider?: string;
  document_date?: string;
  title?: string;
  tags: string[];
  file_size: number;
  mime_type: string;
  uploaded_at: string;
  notes?: string;
  ai_summary?: string;
}

interface QuickReferenceType {
  name: string;
  description: string;
  fields: string[];
}

interface QuickReferenceItem {
  id: string;
  item_type: string;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface RecordsSummary {
  total_documents: number;
  by_category: Record<string, number>;
  recent_uploads: HealthDocument[];
  quick_reference_count: number;
}

type TabType = 'documents' | 'quick-reference' | 'upload';

// Category icons mapping
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  lab_results: BeakerIcon,
  imaging: PhotoIcon,
  visit_summary: ClipboardDocumentListIcon,
  immunization: ShieldCheckIcon,
  medication_list: DocumentDuplicateIcon,
  allergy_info: ExclamationTriangleIcon,
  surgical_history: ScissorsIcon,
  insurance: ShieldCheckIcon,
  advance_directive: DocumentTextIcon,
  other: FolderIcon,
};

// Quick reference icons
const quickRefIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  emergency_contact: PhoneIcon,
  current_medication: DocumentDuplicateIcon,
  allergy: ExclamationTriangleIcon,
  blood_type: HeartIcon,
  condition: ClipboardDocumentListIcon,
  provider: BuildingOffice2Icon,
  vaccination: ShieldCheckIcon,
};

export const HealthRecords: React.FC = () => {
  const { t } = useTranslation();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('documents');

  // Categories state
  const [categories, setCategories] = useState<Record<string, DocumentCategory>>({});
  const [quickRefTypes, setQuickRefTypes] = useState<Record<string, QuickReferenceType>>({});

  // Documents state
  const [documents, setDocuments] = useState<HealthDocument[]>([]);
  const [summary, setSummary] = useState<RecordsSummary | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Quick reference state
  const [quickRefItems, setQuickRefItems] = useState<QuickReferenceItem[]>([]);
  const [showAddQuickRef, setShowAddQuickRef] = useState(false);
  const [newQuickRefType, setNewQuickRefType] = useState('');
  const [newQuickRefData, setNewQuickRefData] = useState<Record<string, string>>({});
  const [vaccinationRecommendations, setVaccinationRecommendations] = useState<Record<string, any>>({});
  const [loadingRecommendation, setLoadingRecommendation] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadProvider, setUploadProvider] = useState('');
  const [uploadDate, setUploadDate] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [autoCategorizeSetting, setAutoCategorizeSetting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingDoc, setEditingDoc] = useState<HealthDocument | null>(null);

  // Load data on mount
  useEffect(() => {
    fetchCategories();
    fetchQuickRefTypes();
    fetchDocuments();
    fetchSummary();
    fetchQuickRefItems();
  }, []);

  // API calls
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health-records/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchQuickRefTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference-types`);
      if (response.ok) {
        const data = await response.json();
        setQuickRefTypes(data.types);
      }
    } catch (error) {
      console.error('Failed to fetch quick reference types:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      let url = `${API_BASE_URL}/health-records/documents`;
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/health-records/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchQuickRefItems = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setQuickRefItems(data.items);
      }
    } catch (error) {
      console.error('Failed to fetch quick reference items:', error);
    }
  };

  const handleImportMedications = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('healthRecords:errors.loginRequired', 'Please log in to import medications'));
        return;
      }

      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference/import-medications`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.imported > 0) {
          setSuccess(t('healthRecords:success.medicationsImported', { count: data.imported, defaultValue: `Imported ${data.imported} medication(s) from My Medications` }));
          fetchQuickRefItems(); // Refresh the list
        } else if (data.skipped > 0) {
          setSuccess(t('healthRecords:success.medicationsAlreadyImported', 'All medications are already in Quick Reference'));
        } else {
          setSuccess(t('healthRecords:success.noMedicationsToImport', 'No active medications found in My Medications'));
        }
      } else {
        throw new Error('Failed to import medications');
      }
    } catch (error) {
      console.error('Import error:', error);
      setError(t('healthRecords:errors.importFailed', 'Failed to import medications'));
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadTitle) {
        setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadFile) {
      setError(t('healthRecords:errors.noFile', 'Please select a file to upload'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('healthRecords:errors.loginRequired', 'Please log in to upload documents'));
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      if (uploadProvider) formData.append('provider', uploadProvider);
      if (uploadDate) formData.append('document_date', uploadDate);
      if (uploadTitle) formData.append('title', uploadTitle);
      if (uploadNotes) formData.append('notes', uploadNotes);
      if (uploadTags.length > 0) formData.append('tags', JSON.stringify(uploadTags));
      formData.append('auto_categorize', String(autoCategorizeSetting));

      const response = await fetch(`${API_BASE_URL}/health-records/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      // Success - no message shown, just reset and refresh
      resetUploadForm();
      fetchDocuments();
      fetchSummary();
      setActiveTab('documents');
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message || t('healthRecords:errors.uploadFailed', 'Failed to upload document'));
    } finally {
      setLoading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadCategory('other');
    setUploadProvider('');
    setUploadDate('');
    setUploadTitle('');
    setUploadNotes('');
    setUploadTags([]);
    setNewTag('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewDocument = async (doc: HealthDocument) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('healthRecords:errors.loginRequired', 'Please log in to view documents'));
        return;
      }

      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/health-records/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Open in new tab/window or download based on mime type
      const viewableTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'text/plain'];

      if (viewableTypes.includes(doc.mime_type)) {
        // Open in new tab for viewable types
        window.open(url, '_blank');
      } else {
        // Download for other types
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      // Cleanup URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('View error:', error);
      setError(t('healthRecords:errors.viewFailed', 'Failed to open document'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm(t('healthRecords:confirm.delete', 'Are you sure you want to delete this document?'))) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/health-records/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setDocuments(documents.filter((d) => d.id !== docId));
        setSuccess(t('healthRecords:success.deleted', 'Document deleted successfully'));
        fetchSummary();
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError(t('healthRecords:errors.deleteFailed', 'Failed to delete document'));
    }
  };

  const handleAddQuickRef = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newQuickRefType) {
      setError(t('healthRecords:errors.selectType', 'Please select a type'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('healthRecords:errors.loginRequired', 'Please log in'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          item_type: newQuickRefType,
          data: newQuickRefData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add item');
      }

      setSuccess(t('healthRecords:success.quickRefAdded', 'Item added successfully'));
      setShowAddQuickRef(false);
      setNewQuickRefType('');
      setNewQuickRefData({});
      fetchQuickRefItems();
    } catch (error) {
      console.error('Add quick ref error:', error);
      setError(t('healthRecords:errors.addFailed', 'Failed to add item'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuickRef = async (itemId: string) => {
    if (!window.confirm(t('healthRecords:confirm.deleteQuickRef', 'Delete this item?'))) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setQuickRefItems(quickRefItems.filter((i) => i.id !== itemId));
        setSuccess(t('healthRecords:success.quickRefDeleted', 'Item deleted'));
      }
    } catch (error) {
      console.error('Delete quick ref error:', error);
    }
  };

  const handleImportFromFile = async () => {
    if (!importFile || !newQuickRefType) {
      setError(t('healthRecords:errors.selectTypeAndFile', 'Please select a type and file to import'));
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('healthRecords:errors.loginRequired', 'Please log in to import'));
        return;
      }

      setImportLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('item_type', newQuickRefType);

      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference/import-from-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        // Success - refresh list without showing message
        if (data.imported > 0) {
          fetchQuickRefItems(); // Refresh the list
        }
        // Close the dialog after import
        setShowAddQuickRef(false);
        setNewQuickRefType('');
        setNewQuickRefData({});
        setImportFile(null);
        if (importFileInputRef.current) {
          importFileInputRef.current.value = '';
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import failed');
      }
    } catch (error: any) {
      console.error('Import from file error:', error);
      setError(error.message || t('healthRecords:errors.importFileFailed', 'Failed to import from file'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
    }
  };

  const fetchVaccinationRecommendation = async (itemId: string, vaccineName: string, dateAdministered: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      setLoadingRecommendation(itemId);

      const response = await fetch(`${API_BASE_URL}/health-records/quick-reference/vaccination-recommendation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vaccine_name: vaccineName,
          date_administered: dateAdministered,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setVaccinationRecommendations((prev) => ({
          ...prev,
          [itemId]: data.recommendation,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch vaccination recommendation:', error);
    } finally {
      setLoadingRecommendation(null);
    }
  };

  // Generate plain text content for Quick Reference export
  const generateQuickRefTextContent = (): string => {
    const currentDate = new Date().toLocaleDateString();
    const title = t('healthRecords:quickRef.exportTitle', 'Quick Reference Summary');
    let content = `${title}\n`;
    content += `${t('healthRecords:quickRef.generatedOn', 'Generated on')}: ${currentDate}\n`;
    content += '='.repeat(50) + '\n\n';

    // Loop through each quick reference type and add items
    Object.entries(quickRefTypes).forEach(([typeKey, typeInfo]) => {
      const itemsOfType = quickRefItems.filter((item) => item.item_type === typeKey);

      content += `### ${typeInfo.name}\n`;
      content += '-'.repeat(30) + '\n';

      if (itemsOfType.length === 0) {
        content += `${t('healthRecords:quickRef.noItems', 'No items added yet')}\n`;
      } else {
        itemsOfType.forEach((item, index) => {
          if (index > 0) content += '\n';
          Object.entries(item.data).forEach(([key, value]) => {
            if (value) {
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
              content += `${label}: ${value}\n`;
            }
          });
        });
      }

      content += '\n';
    });

    content += '='.repeat(50) + '\n';
    content += t('healthRecords:disclaimer.text', 'Keep your health records secure. This app does not replace official medical records from your healthcare providers.') + '\n';

    return content;
  };

  // Generate HTML content for print
  const generateQuickRefHtmlContent = (): string => {
    const currentDate = new Date().toLocaleDateString();
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${t('healthRecords:quickRef.exportTitle', 'Quick Reference Summary')}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 {
            text-align: center;
            color: #059669;
            border-bottom: 2px solid #059669;
            padding-bottom: 10px;
            margin-bottom: 30px;
          }
          h2 {
            color: #047857;
            margin-top: 30px;
            margin-bottom: 15px;
            padding-bottom: 5px;
            border-bottom: 1px solid #d1d5db;
          }
          .section {
            margin-bottom: 25px;
          }
          .item {
            background: #f3f4f6;
            padding: 12px 16px;
            margin-bottom: 10px;
            border-radius: 8px;
            border-left: 4px solid #059669;
          }
          .item-field {
            margin-bottom: 4px;
          }
          .field-label {
            font-weight: 600;
            color: #374151;
            text-transform: capitalize;
          }
          .field-value {
            color: #4b5563;
          }
          .empty-section {
            color: #9ca3af;
            font-style: italic;
            padding: 10px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #d1d5db;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
          }
          .generated-date {
            text-align: center;
            color: #6b7280;
            font-size: 14px;
            margin-bottom: 30px;
          }
          @media print {
            body { padding: 20px; }
            .item { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>${t('healthRecords:quickRef.exportTitle', 'Quick Reference Summary')}</h1>
        <div class="generated-date">${t('healthRecords:quickRef.generatedOn', 'Generated on')}: ${currentDate}</div>
    `;

    // Loop through each quick reference type and add items
    Object.entries(quickRefTypes).forEach(([typeKey, typeInfo]) => {
      const itemsOfType = quickRefItems.filter((item) => item.item_type === typeKey);

      html += `<div class="section">`;
      html += `<h2>${typeInfo.name}</h2>`;

      if (itemsOfType.length === 0) {
        html += `<div class="empty-section">${t('healthRecords:quickRef.noItems', 'No items added yet')}</div>`;
      } else {
        itemsOfType.forEach((item) => {
          html += `<div class="item">`;
          Object.entries(item.data).forEach(([key, value]) => {
            if (value) {
              html += `<div class="item-field">
                <span class="field-label">${key.replace(/_/g, ' ')}:</span>
                <span class="field-value"> ${value}</span>
              </div>`;
            }
          });
          html += `</div>`;
        });
      }

      html += `</div>`;
    });

    html += `
        <div class="footer">
          <p>${t('healthRecords:disclaimer.text', 'Keep your health records secure. This app does not replace official medical records from your healthcare providers.')}</p>
        </div>
      </body>
      </html>
    `;

    return html;
  };

  // Handle export from dropdown menu
  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };
    await exportQuickRefDocument(format);
  };

  // Print Quick Reference
  const handlePrintQuickRef = () => {
    const printContent = generateQuickRefHtmlContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Export Quick Reference as Word, PDF, or Text file
  const exportQuickRefDocument = async (format: 'word' | 'pdf' | 'text') => {
    try {
      const content = generateQuickRefTextContent();
      const title = t('healthRecords:quickRef.exportTitle', 'Quick Reference Summary');
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `quick-reference-${new Date().toISOString().split('T')[0]}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: extension as 'docx' | 'pdf' | 'txt',
      });

      // Try to use File System Access API for better UX
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain',
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File',
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: descriptions[format],
                accept: { [mimeTypes[extension]]: [`.${extension}`] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          setSuccess(t('healthRecords:success.exported', 'Quick Reference exported successfully'));
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') {
            return; // User cancelled
          }
          console.warn('File System Access API failed, falling back to standard download:', err);
        }
      }

      // Fallback: Standard download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess(t('healthRecords:success.exported', 'Quick Reference exported successfully'));
    } catch (error) {
      console.error('Export error:', error);
      setError(t('healthRecords:errors.exportFailed', 'Failed to export document. Please try again.'));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !uploadTags.includes(newTag.trim())) {
      setUploadTags([...uploadTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setUploadTags(uploadTags.filter((t) => t !== tag));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryIcon = (category: string) => {
    const IconComponent = categoryIcons[category] || FolderIcon;
    return <IconComponent className="h-5 w-5" />;
  };

  const getQuickRefIcon = (type: string) => {
    const IconComponent = quickRefIcons[type] || DocumentTextIcon;
    return <IconComponent className="h-5 w-5" />;
  };

  // Re-fetch documents when filters change
  useEffect(() => {
    fetchDocuments();
  }, [filterCategory, searchQuery]);

  return (
    <div className="flex flex-col h-full p-6">
      {/* Loading Overlay for uploads */}
      <LoadingOverlay
        isVisible={loading || importLoading}
        message={t('healthRecords:loading.uploading', 'Uploading...')}
        subMessage={t('healthRecords:loading.pleaseWait', 'Please wait while we process your file')}
      />

      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <FolderIcon className="h-12 w-12 text-emerald-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('healthRecords:title', 'Health Records')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('healthRecords:subtitle', 'Organize and manage your health documents')}
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 max-w-5xl mx-auto w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-emerald-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{summary.total_documents}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('healthRecords:summary.documents', 'Documents')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <FolderIcon className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{Object.keys(summary.by_category).length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('healthRecords:summary.categories', 'Categories')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-purple-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{summary.quick_reference_count}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('healthRecords:summary.quickRef', 'Quick Refs')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-orange-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{summary.recent_uploads.length}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('healthRecords:summary.recent', 'Recent')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'documents'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <FolderIcon className="h-4 w-4 inline mr-1" />
            {t('healthRecords:tabs.documents', 'Documents')}
          </button>
          <button
            onClick={() => setActiveTab('quick-reference')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'quick-reference'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <CheckCircleIcon className="h-4 w-4 inline mr-1" />
            {t('healthRecords:tabs.quickReference', 'Quick Reference')}
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <DocumentArrowUpIcon className="h-4 w-4 inline mr-1" />
            {t('healthRecords:tabs.upload', 'Upload')}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-5xl mx-auto w-full mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="max-w-5xl mx-auto w-full mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-700 dark:text-green-300 text-sm">{success}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto max-w-5xl mx-auto w-full">
        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            {/* Search and Filter Bar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('healthRecords:search.placeholder', 'Search documents...')}
                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">{t('healthRecords:filter.allCategories', 'All Categories')}</option>
                    {Object.entries(categories).map(([key, cat]) => (
                      <option key={key} value={key}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
                  >
                    <PlusIcon className="h-5 w-5 mr-1" />
                    {t('healthRecords:actions.upload', 'Upload')}
                  </button>
                </div>
              </div>
            </div>

            {/* Documents List */}
            {documents.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                <FolderIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">{t('healthRecords:empty.noDocuments', 'No documents yet')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">{t('healthRecords:empty.uploadFirst', 'Upload your first document to get started')}</p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  {t('healthRecords:actions.uploadFirst', 'Upload Document')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400">
                          {getCategoryIcon(doc.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{doc.title || doc.filename}</h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600 dark:text-gray-400">
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                              {categories[doc.category]?.name || doc.category}
                            </span>
                            {doc.provider && (
                              <span className="flex items-center">
                                <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                                {doc.provider}
                              </span>
                            )}
                            {doc.document_date && (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(doc.document_date).toLocaleDateString()}
                              </span>
                            )}
                            <span className="text-gray-500">{formatFileSize(doc.file_size)}</span>
                          </div>
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {doc.tags.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {doc.ai_summary && (
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">{doc.ai_summary}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 ml-4">
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="p-2 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                          title={t('healthRecords:actions.view', 'View')}
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title={t('healthRecords:actions.delete', 'Delete')}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Reference Tab */}
        {activeTab === 'quick-reference' && (
          <div className="space-y-4">
            {/* Quick Reference Action Buttons */}
            <div className="flex justify-between items-center">
              {/* Left side - Export/Print dropdown */}
              <ExportDropdownMenu onExport={handleExport} onPrint={handlePrintQuickRef} showPrint={true} />

              {/* Right side - Import and Add buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleImportMedications}
                  disabled={loading}
                  className="px-4 py-2 border border-emerald-600 text-emerald-600 dark:text-emerald-400 dark:border-emerald-400 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center disabled:opacity-50"
                  title={t('healthRecords:quickRef.importMedicationsTooltip', 'Import active medications from My Medications')}
                >
                  <ArrowPathIcon className="h-5 w-5 mr-1" />
                  {t('healthRecords:quickRef.importMedications', 'Import from My Medications')}
                </button>
                <button
                  onClick={() => setShowAddQuickRef(true)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-1" />
                  {t('healthRecords:quickRef.add', 'Add Item')}
                </button>
              </div>
            </div>

            {/* Quick Reference Categories */}
            {Object.entries(quickRefTypes).map(([typeKey, typeInfo]) => {
              const itemsOfType = quickRefItems.filter((item) => item.item_type === typeKey);

              return (
                <div key={typeKey} className="bg-white dark:bg-gray-800 rounded-lg shadow">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400 mr-3">
                        {getQuickRefIcon(typeKey)}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{typeInfo.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{typeInfo.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    {itemsOfType.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-sm italic">{t('healthRecords:quickRef.noItems', 'No items added yet')}</p>
                    ) : (
                      <div className="space-y-2">
                        {itemsOfType.map((item) => (
                          <div key={item.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                {Object.entries(item.data).map(([key, value]) => (
                                  <span key={key} className="mr-4 text-sm text-gray-700 dark:text-gray-300">
                                    <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {value}
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Show "Get Recommendation" button for vaccinations */}
                                {typeKey === 'vaccination' && item.data.vaccine_name && item.data.date_administered && (
                                  <button
                                    onClick={() => fetchVaccinationRecommendation(item.id, item.data.vaccine_name, item.data.date_administered)}
                                    disabled={loadingRecommendation === item.id}
                                    className="px-2 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded hover:bg-emerald-200 dark:hover:bg-emerald-900/50 disabled:opacity-50"
                                    title={t('healthRecords:vaccination.getRecommendation', 'Get next dose recommendation')}
                                  >
                                    {loadingRecommendation === item.id ? (
                                      <span className="flex items-center">
                                        <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                        {t('healthRecords:vaccination.loading', 'Loading...')}
                                      </span>
                                    ) : (
                                      t('healthRecords:vaccination.nextDose', 'Next Dose?')
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteQuickRef(item.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            {/* Show vaccination recommendation if available */}
                            {typeKey === 'vaccination' && vaccinationRecommendations[item.id] && (
                              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                                  {t('healthRecords:vaccination.recommendationTitle', 'Vaccination Recommendation')}
                                </h4>
                                <div className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                                  <p>
                                    <span className="font-medium">{t('healthRecords:vaccination.nextDoseDue', 'Next Dose Due')}:</span>{' '}
                                    {vaccinationRecommendations[item.id].next_dose_due}
                                  </p>
                                  <p>
                                    <span className="font-medium">{t('healthRecords:vaccination.recommendation', 'Recommendation')}:</span>{' '}
                                    {vaccinationRecommendations[item.id].recommendation}
                                  </p>
                                  {vaccinationRecommendations[item.id].notes && (
                                    <p className="text-xs italic mt-2">{vaccinationRecommendations[item.id].notes}</p>
                                  )}
                                </div>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 italic">
                                  {t('healthRecords:vaccination.disclaimer', 'Consult your healthcare provider for personalized advice.')}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add Quick Reference Modal */}
            {showAddQuickRef && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {t('healthRecords:quickRef.addTitle', 'Add Quick Reference')}
                    </h3>
                    <button
                      onClick={() => {
                        setShowAddQuickRef(false);
                        setImportFile(null);
                        if (importFileInputRef.current) {
                          importFileInputRef.current.value = '';
                        }
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Type Selection - Shared between Manual and Import */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('healthRecords:quickRef.type', 'Type')}
                    </label>
                    <select
                      value={newQuickRefType}
                      onChange={(e) => {
                        setNewQuickRefType(e.target.value);
                        setNewQuickRefData({});
                        setImportFile(null);
                        if (importFileInputRef.current) {
                          importFileInputRef.current.value = '';
                        }
                      }}
                      className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      required
                    >
                      <option value="">{t('healthRecords:quickRef.selectType', 'Select type...')}</option>
                      {Object.entries(quickRefTypes).map(([key, type]) => (
                        <option key={key} value={key}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Import from File Section */}
                  {newQuickRefType && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                        <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                        {t('healthRecords:quickRef.importFromFile', 'Import from File')}
                      </h4>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                        {t('healthRecords:quickRef.importDescription', 'Upload a PDF, Word, or text file containing your records. AI will extract the information automatically.')}
                      </p>
                      <div className="flex flex-col gap-2">
                        <input
                          ref={importFileInputRef}
                          type="file"
                          onChange={handleImportFileSelect}
                          accept=".pdf,.doc,.docx,.txt"
                          className="hidden"
                        />
                        {importFile ? (
                          <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded border border-blue-300 dark:border-blue-600">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[200px]">{importFile.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setImportFile(null);
                                if (importFileInputRef.current) {
                                  importFileInputRef.current.value = '';
                                }
                              }}
                              className="p-1 text-gray-400 hover:text-red-500"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => importFileInputRef.current?.click()}
                            className="w-full px-4 py-2 border-2 border-dashed border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm"
                          >
                            {t('healthRecords:quickRef.selectFile', 'Select File (PDF, Word, TXT)')}
                          </button>
                        )}
                        {importFile && (
                          <button
                            type="button"
                            onClick={handleImportFromFile}
                            disabled={importLoading}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm flex items-center justify-center"
                          >
                            {importLoading ? (
                              <>
                                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                {t('healthRecords:quickRef.importing', 'Importing...')}
                              </>
                            ) : (
                              <>
                                <ArrowPathIcon className="h-4 w-4 mr-2" />
                                {t('healthRecords:quickRef.importButton', 'Import from File')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  {newQuickRefType && (
                    <div className="relative mb-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                          {t('healthRecords:quickRef.orManualEntry', 'or enter manually')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Manual Entry Form */}
                  <form onSubmit={handleAddQuickRef} className="space-y-4">
                    {newQuickRefType && quickRefTypes[newQuickRefType] && (
                      <>
                        {quickRefTypes[newQuickRefType].fields.map((field) => {
                          const isDateField = field.toLowerCase().includes('date');
                          return (
                            <div key={field}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                                {field.replace(/_/g, ' ')}
                              </label>
                              <div className="relative">
                                <input
                                  type={isDateField ? 'date' : 'text'}
                                  value={newQuickRefData[field] || ''}
                                  onChange={(e) => setNewQuickRefData({ ...newQuickRefData, [field]: e.target.value })}
                                  className={`w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 ${isDateField ? 'dark:[color-scheme:dark]' : ''}`}
                                />
                                {isDateField && (
                                  <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddQuickRef(false);
                          setImportFile(null);
                          if (importFileInputRef.current) {
                            importFileInputRef.current.value = '';
                          }
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {t('common:cancel', 'Cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !newQuickRefType}
                        className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
                      >
                        {loading ? t('common:saving', 'Saving...') : t('common:save', 'Save')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <form onSubmit={handleUpload} className="space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('healthRecords:upload.selectFile', 'Select File')} *
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                  />
                  {uploadFile ? (
                    <div className="flex items-center justify-center space-x-4">
                      <DocumentTextIcon className="h-12 w-12 text-emerald-500" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{uploadFile.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{formatFileSize(uploadFile.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUploadFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <DocumentArrowUpIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        {t('healthRecords:upload.chooseFile', 'Choose File')}
                      </button>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {t('healthRecords:upload.supportedFormats', 'PDF, Word, Images (max 100MB)')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('healthRecords:upload.category', 'Category')} *
                </label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  {Object.entries(categories).map(([key, cat]) => (
                    <option key={key} value={key}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {categories[uploadCategory]?.description}
                </p>
              </div>

              {/* Title & Provider Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('healthRecords:upload.title', 'Title')}
                  </label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder={t('healthRecords:upload.titlePlaceholder', 'Document title')}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('healthRecords:upload.provider', 'Healthcare Provider')}
                  </label>
                  <input
                    type="text"
                    value={uploadProvider}
                    onChange={(e) => setUploadProvider(e.target.value)}
                    placeholder={t('healthRecords:upload.providerPlaceholder', 'e.g., Dr. Smith, City Hospital')}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Document Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('healthRecords:upload.documentDate', 'Document Date')}
                </label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:[color-scheme:dark]"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('healthRecords:upload.tags', 'Tags')}
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {uploadTags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-sm flex items-center"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-2">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder={t('healthRecords:upload.addTag', 'Add a tag...')}
                    className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('healthRecords:upload.notes', 'Notes')}
                </label>
                <textarea
                  value={uploadNotes}
                  onChange={(e) => setUploadNotes(e.target.value)}
                  placeholder={t('healthRecords:upload.notesPlaceholder', 'Add any notes about this document...')}
                  rows={3}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              {/* Auto-categorize option */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="auto-categorize"
                  checked={autoCategorizeSetting}
                  onChange={(e) => setAutoCategorizeSetting(e.target.checked)}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <label htmlFor="auto-categorize" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('healthRecords:upload.autoCategorize', 'Use AI to auto-categorize and suggest tags')}
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !uploadFile}
                className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? t('healthRecords:upload.uploading', 'Uploading...') : t('healthRecords:upload.uploadDocument', 'Upload Document')}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-5xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('healthRecords:disclaimer.title', 'Important')}:</strong> {t('healthRecords:disclaimer.text', 'Keep your health records secure. This app does not replace official medical records from your healthcare providers.')}
        </p>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('healthRecords:loading', 'Loading...')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
